import React from 'react';
import { Field } from 'react-final-form';
import { Divider } from '@chakra-ui/react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useParams } from 'react-router-dom';
import { ValidationErrors } from 'final-form';
import { NIL } from 'uuid';
import MarkdownTextInput from './MarkdownTextInput';
import DateInputControl from './DateInput';
import TextInputControl from './TextInput';
import CurrencyInputControl from './CurrencyInput';
import SelectInputControl from './SelectInput';
import FormRow from './FormRow';
import ResponsiveStack from '../ResponsiveStack';
import DocumentFormScaffold from './DocumentFormScaffold';
import ServiceAddressSection from './ServiceAddressSection';
import ProjectSelectField from './ProjectSelectField';
import ViewPdfButton from './ViewPdfButton';
import IconBill from '../../assets/Icon-Bills.svg';
import IconBillWhite from '../../assets/Icon-Bills-White.svg';
import useContacts from '../../hooks/useContacts';
import { ContactDto } from '../../models/contactInfo';
import useUsers from '../../hooks/useUsers';
import { UserDto } from '../../models/user';
import useBill from '../../hooks/useBill';
import { Bill, BillItems } from '../../models/bill';
import BillItemsFormElement from '../BillItemsFormElement';

const validate = (values: Bill): ValidationErrors => {
  const errors: ValidationErrors = {};
  // item 465 — the Service Address name is required
  if (!values.serviceName) {
    errors.serviceName = 'Required';
  }
  // number is optional — left blank, the backend auto-generates it (item 101)
  if (!values.date) {
    errors.date = 'Required';
  }
  if (!values.dueDate) {
    errors.dueDate = 'Required';
  }
  // every document must belong to a project (Chad, 2026-06-22)
  if (!values.projectId) {
    errors.projectId = 'Required';
  }
  return errors;
};

const BillForm: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const { tryUpdate, data: bill, status } = useBill({ id });
  const { data: contacts, status: contactStatus } = useContacts();
  const { data: users, status: usersStatus } = useUsers();

  const infoSection = (
    <>
      {/* item 465 — Service Address (end-user / delivery) FIRST; bills carry their own serviceName */}
      <ServiceAddressSection nameField="serviceName" />
      <Divider my={4} />
      <FormRow
        heading="Basic Information"
        subHeading="Information that will be the main identifiers for the bill"
      >
        <ResponsiveStack>
          <TextInputControl
            name="billNumber"
            label="Bill Number"
            isRequired={false}
            placeholder="Uses your standardized numbering — fill in to override with a custom number"
          />
          <SelectInputControl name="customerId" label="Customer" isRequired>
            {contactStatus === 'success' && contacts && contacts.index.map((contact: ContactDto) => <option key={contact.id} value={contact.id}>{contact.name}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        <ResponsiveStack>
          <DateInputControl
            name="dueDate"
            label="Due Date"
            isRequired
          />
          <DateInputControl
            name="date"
            label="Date"
            isRequired
          />
        </ResponsiveStack>
        <ResponsiveStack>
          <ProjectSelectField name="projectId" label="Project" />
          <SelectInputControl name="salesRepId" label="Sales Rep">
            {usersStatus === 'success' && users
            // eslint-disable-next-line max-len
            && users.index.map((user: UserDto) => <option key={user.id} value={user.id}>{user.name.toUpperCase()}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        <ResponsiveStack>
          <CurrencyInputControl
            name="discount"
            label="Discount"
          />
          <TextInputControl
            name="discountReason"
            label="Discount Reason"
          />
        </ResponsiveStack>
      </FormRow>

      <Divider my={4} />
      <FormRow
        heading="Internal Notes"
        subHeading="Internal Notes Of The Work"
      >
        <Field<string>
          name="internalNotes"
          component={MarkdownTextInput}
          label="Internal Notes"
        />
      </FormRow>
    </>
  );

  return (
    <DocumentFormScaffold<Bill, BillItems>
      config={{
        title: 'BILL',
        saveLabel: 'Save Bill',
        icon: IconBill,
        iconWhite: IconBillWhite,
        doc: bill,
        status,
        tryUpdate,
        validate,
        getItems: (doc) => doc.billItems,
        getCategoryNames: (doc) => doc.categoryNames,
        applyScaffoldState: (values, state) => {
          values.categoryNames = state.categoryNames;
          values.billItems = state.items;
          values.id = state.id;
        },
        ItemsElement: BillItemsFormElement,
        infoSection,
        footerActions: id !== NIL ? (
          <>
            <ViewPdfButton path={`bills/${id}/pdf`} preview />
            <ViewPdfButton path={`bills/${id}/pdf`} />
          </>
        ) : undefined,
      }}
    />
  );
};

export default withAuthenticationRequired(BillForm);
